/**
 * SchoolFees - Fee Management Module
 * Tabs: Dashboard, Fee Types, Structures, Payments, Student Accounts, Defaulters, Carry Forward
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Loader2, Plus, DollarSign, Users, AlertTriangle, Receipt, ArrowRightLeft,
  Trash2, Search, TrendingUp, CreditCard, Banknote, Smartphone, FileText,
  Download, Lock, Unlock, Shield, Settings
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useAcademicContext } from '@/components/school/AcademicContext';
import { getToken } from '@/lib/authToken';
import { formatNumber } from '@/lib/currency';
import { useCampusContext } from '@/components/school/CampusContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, {
    ...opts,
    headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};

const downloadFile = async (path, filename) => {
  try {
    const res = await fetch(`${API_URL}/api/school${path}`, {
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filename}`);
  } catch (e) { toast.error(e.message); }
};

const CATEGORIES = [
  { value: 'tuition', label: 'Tuition' },
  { value: 'boarding', label: 'Boarding' },
  { value: 'transport', label: 'Transport' },
  { value: 'lab', label: 'Lab Fee' },
  { value: 'library', label: 'Library' },
  { value: 'exam', label: 'Exam Fee' },
  { value: 'uniform', label: 'Uniform' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash', icon: Banknote },
  { value: 'bank_transfer', label: 'Bank Transfer', icon: CreditCard },
  { value: 'mobile_money', label: 'Mobile Money', icon: Smartphone },
  { value: 'cheque', label: 'Cheque', icon: FileText },
  { value: 'other', label: 'Other', icon: DollarSign },
];

const fmt = (n) => formatNumber(n);

// ==================== FEE DASHBOARD ====================
const FeeDashboard = ({ tenantId }) => {
  const { termId: feeTermId, term: feeTerm } = useAcademicContext(tenantId);
  const { data: dashboard, isLoading } = useQuery({
    queryKey: ['fee-dashboard', tenantId, feeTermId],
    queryFn: () => api(`/tenants/${tenantId}/fee-dashboard${feeTermId ? `?term_id=${feeTermId}` : ''}`),
    enabled: !!tenantId,
  });

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>;
  if (!dashboard) return null;

  const stats = [
    { label: 'Total Expected', value: fmt(dashboard.total_expected), color: 'text-slate-900', bg: 'bg-slate-50' },
    { label: 'Collected', value: fmt(dashboard.total_collected), color: 'text-emerald-700', bg: 'bg-emerald-50' },
    { label: 'Outstanding', value: fmt(dashboard.outstanding), color: 'text-amber-700', bg: 'bg-amber-50' },
    { label: 'Collection Rate', value: `${dashboard.collection_rate}%`, color: 'text-blue-700', bg: 'bg-blue-50' },
    { label: 'Total Students', value: dashboard.total_students, color: 'text-indigo-700', bg: 'bg-indigo-50' },
    { label: 'Defaulters', value: dashboard.defaulters, color: 'text-red-700', bg: 'bg-red-50' },
  ];

  return (
    <div className="space-y-6" data-testid="fee-dashboard">
      {/* Export buttons */}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => downloadFile(`/tenants/${tenantId}/fee-reports/class-collection`, 'class_collection_report.csv')} data-testid="export-class-collection">
          <Download className="h-3.5 w-3.5 mr-1" /> Class Collection (CSV)
        </Button>
        <Button variant="outline" size="sm" onClick={() => downloadFile(`/tenants/${tenantId}/fee-reports/term-summary`, 'term_fee_summary.csv')} data-testid="export-term-summary">
          <Download className="h-3.5 w-3.5 mr-1" /> Term Summary (CSV)
        </Button>
        <Button variant="outline" size="sm" onClick={() => downloadFile(`/tenants/${tenantId}/fee-reports/defaulters-csv`, 'defaulters_report.csv')} data-testid="export-defaulters">
          <Download className="h-3.5 w-3.5 mr-1" /> Defaulters (CSV)
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {stats.map(s => (
          <Card key={s.label} className={s.bg}>
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-xs text-slate-500 mb-1">{s.label}</p>
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Payment method breakdown */}
      {Object.keys(dashboard.payment_methods || {}).length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Payment Methods</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {Object.entries(dashboard.payment_methods).map(([method, amount]) => (
                <Badge key={method} variant="outline" className="px-3 py-1.5 text-sm">
                  {method.replace(/_/g, ' ')}: <span className="font-bold ml-1">{fmt(amount)}</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent payments */}
      {dashboard.recent_payments?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Recent Payments</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Receipt</TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dashboard.recent_payments.map(p => (
                  <TableRow key={p.id}>
                    <TableCell className="font-mono text-xs">{p.receipt_number}</TableCell>
                    <TableCell>{p.student_name || '-'}</TableCell>
                    <TableCell className="font-semibold text-emerald-700">{fmt(p.amount)}</TableCell>
                    <TableCell><Badge variant="outline">{(p.payment_method || '').replace(/_/g, ' ')}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-500">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== FEE TYPES ====================
const FeeTypes = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', code: '', category: 'tuition', description: '', default_amount: 0, is_recurring: true, frequency: 'per_term' });

  const { data: types = [], isLoading } = useQuery({
    queryKey: ['fee-types', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/fee-types`),
    enabled: !!tenantId,
  });

  const createMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/fee-types`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { toast.success('Fee type created'); setShowForm(false); setForm({ name: '', code: '', category: 'tuition', description: '', default_amount: 0, is_recurring: true, frequency: 'per_term' }); queryClient.invalidateQueries({ queryKey: ['fee-types'] }); },
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/fee-types/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Fee type deleted'); queryClient.invalidateQueries({ queryKey: ['fee-types'] }); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-4" data-testid="fee-types-panel">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Fee Types</h2>
        <Button size="sm" onClick={() => setShowForm(true)} data-testid="add-fee-type-btn"><Plus className="h-4 w-4 mr-1" /> Add Fee Type</Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Default Amount</TableHead>
              <TableHead>Frequency</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
            ) : types.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No fee types defined yet</TableCell></TableRow>
            ) : types.map(t => (
              <TableRow key={t.id}>
                <TableCell className="font-medium">{t.name}</TableCell>
                <TableCell><Badge variant="outline">{t.code}</Badge></TableCell>
                <TableCell className="capitalize">{t.category}</TableCell>
                <TableCell>{fmt(t.default_amount)}</TableCell>
                <TableCell className="capitalize">{(t.frequency || '').replace(/_/g, ' ')}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => deleteMutation.mutate(t.id)} className="text-red-400 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Fee Type</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Tuition Fee" data-testid="fee-type-name" /></div>
            <div><Label>Code *</Label><Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="e.g. TUI" data-testid="fee-type-code" /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Default Amount</Label><Input type="number" value={form.default_amount} onChange={e => setForm({ ...form, default_amount: parseFloat(e.target.value) || 0 })} min={0} step={0.01} /></div>
            <div>
              <Label>Frequency</Label>
              <Select value={form.frequency} onValueChange={v => setForm({ ...form, frequency: v, is_recurring: v !== 'one_time' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_term">Per Term</SelectItem>
                  <SelectItem value="per_year">Per Year</SelectItem>
                  <SelectItem value="one_time">One Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter className="mt-4">
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => createMutation.mutate(form)} disabled={createMutation.isPending || !form.name || !form.code} data-testid="submit-fee-type-btn">
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== FEE STRUCTURES ====================
const FeeStructures = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null); // selected structure for assign dialog
  const [form, setForm] = useState({ fee_type_id: '', class_id: '', academic_year_id: '', term_id: '', amount: 0, due_date: '', is_mandatory: true });

  const { termId: structTermId, term: structTerm, semesters: structSemesters } = useAcademicContext(tenantId);
  const [showAllSemesters, setShowAllSemesters] = useState(false);
  const { data: structures = [], isLoading } = useQuery({
    queryKey: ['fee-structures', tenantId, structTermId, showAllSemesters],
    queryFn: () => api(`/tenants/${tenantId}/fee-structures${(!showAllSemesters && structTermId) ? `?term_id=${structTermId}` : ''}`),
    enabled: !!tenantId,
  });
  const { data: feeTypes = [] } = useQuery({ queryKey: ['fee-types', tenantId], queryFn: () => api(`/tenants/${tenantId}/fee-types`), enabled: !!tenantId });
  const { data: classes = [] } = useQuery({ queryKey: ['school-classes', tenantId], queryFn: () => api(`/tenants/${tenantId}/classes`), enabled: !!tenantId });
  const { data: years = [] } = useQuery({ queryKey: ['school-academic-years', tenantId], queryFn: () => api(`/tenants/${tenantId}/academic-years`), enabled: !!tenantId });
  const { data: terms = [] } = useQuery({ queryKey: ['school-terms', tenantId], queryFn: () => api(`/tenants/${tenantId}/terms`), enabled: !!tenantId });

  const createMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/fee-structures`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { toast.success('Fee structure created'); setShowForm(false); queryClient.invalidateQueries({ queryKey: ['fee-structures'] }); },
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/fee-structures/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Deleted'); queryClient.invalidateQueries({ queryKey: ['fee-structures'] }); },
    onError: (e) => toast.error(e.message),
  });

  // Auto-fill amount from fee type
  const handleFeeTypeChange = (ftId) => {
    const ft = feeTypes.find(f => f.id === ftId);
    setForm({ ...form, fee_type_id: ftId, amount: ft?.default_amount || form.amount });
  };

  return (
    <div className="space-y-4" data-testid="fee-structures-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold">Fee Structures</h2>
          {structSemesters.length > 0 && (
            <p className="text-xs text-slate-500" data-testid="fee-structures-scope">
              {showAllSemesters
                ? `Showing every semester's structures (${structSemesters.length} semesters)`
                : `Showing ${structTerm?.name || 'the selected semester'} + annual structures — each semester keeps its own`}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {structSemesters.length > 1 && (
            <Button size="sm" variant="outline" onClick={() => setShowAllSemesters(v => !v)}
                    data-testid="toggle-all-semesters-fees">
              {showAllSemesters ? 'This semester only' : 'Show all semesters'}
            </Button>
          )}
          <Button size="sm" onClick={() => { setForm(f => ({ ...f, term_id: f.term_id || structTermId || '' })); setShowForm(true); }}
                  disabled={feeTypes.length === 0} data-testid="add-fee-structure-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Structure
          </Button>
        </div>
      </div>
      {feeTypes.length === 0 && <p className="text-sm text-amber-600">Create fee types first before adding structures.</p>}

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fee Type</TableHead>
              <TableHead>Class</TableHead>
              <TableHead>Semester</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
            ) : structures.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No fee structures yet</TableCell></TableRow>
            ) : structures.map(s => (
              <TableRow key={s.id}>
                <TableCell><span className="font-medium">{s.fee_type_name}</span><Badge variant="outline" className="ml-2 text-xs">{s.fee_type_code}</Badge></TableCell>
                <TableCell>{s.class_name}</TableCell>
                <TableCell>{s.term_name || 'Annual'}</TableCell>
                <TableCell className="font-semibold">{fmt(s.amount)}</TableCell>
                <TableCell className="text-sm">{s.due_date || '-'}</TableCell>
                <TableCell className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => setAssignTarget(s)}
                          data-testid={`assign-fee-${s.id}`}>
                    <Users className="h-3 w-3 mr-1" /> Assign…
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => deleteMutation.mutate(s.id)} className="text-red-400 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Fee Structure</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2">
              <Label>Fee Type *</Label>
              <Select value={form.fee_type_id} onValueChange={handleFeeTypeChange}>
                <SelectTrigger data-testid="structure-fee-type"><SelectValue placeholder="Select fee type" /></SelectTrigger>
                <SelectContent>{feeTypes.map(f => <SelectItem key={f.id} value={f.id}>{f.name} ({f.code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Class (optional)</Label>
              <Select value={form.class_id || '__all__'} onValueChange={v => setForm({ ...form, class_id: v === '__all__' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="All classes" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Classes</SelectItem>
                  {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Academic Year *</Label>
              <Select value={form.academic_year_id} onValueChange={v => setForm({ ...form, academic_year_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select year" /></SelectTrigger>
                <SelectContent>{years.map(y => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Term (optional)</Label>
              <Select value={form.term_id || '__annual__'} onValueChange={v => setForm({ ...form, term_id: v === '__annual__' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Annual" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__annual__">Annual</SelectItem>
                  {terms.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount *</Label>
              <Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} min={0} step={0.01} data-testid="structure-amount" />
            </div>
            <div>
              <Label>Due Date</Label>
              <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => createMutation.mutate(form)} disabled={createMutation.isPending || !form.fee_type_id || !form.academic_year_id || !form.amount} data-testid="submit-fee-structure-btn">
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AssignFeeDialog
        tenantId={tenantId}
        structure={assignTarget}
        onClose={() => setAssignTarget(null)}
        onAssigned={() => { setAssignTarget(null); queryClient.invalidateQueries({ queryKey: ['fee-dashboard'] }); }}
      />
    </div>
  );
};

// ==================== ASSIGN FEE DIALOG (with per-student currency) ====================
const AssignFeeDialog = ({ tenantId, structure, onClose, onAssigned }) => {
  const open = !!structure;
  const [scope, setScope] = useState('class'); // 'class' | 'specific'
  const [classId, setClassId] = useState('');
  const [defaultCurrency, setDefaultCurrency] = useState('');
  const [selectedStudents, setSelectedStudents] = useState({}); // {sid: {amount, currency}}

  React.useEffect(() => {
    if (structure) {
      setScope(structure.class_id ? 'class' : 'specific');
      setClassId(structure.class_id || '');
      setDefaultCurrency('');
      setSelectedStudents({});
    }
  }, [structure]);

  const { data: currencies = [] } = useQuery({
    queryKey: ['fx-currencies'],
    queryFn: () => api('/fx/currencies').catch(() => []),
    enabled: open,
  });

  const { data: classes = [] } = useQuery({
    queryKey: ['school-classes', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/classes`),
    enabled: !!tenantId && open,
  });

  const { data: students = [] } = useQuery({
    queryKey: ['school-students-list', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/students?limit=1000`),
    enabled: !!tenantId && open,
  });

  // Filter students to picker — by class (if scope=class) or all (specific)
  const studentList = React.useMemo(() => {
    const arr = Array.isArray(students) ? students : (students.items || students.students || []);
    if (scope === 'class' && classId) return arr.filter(s => s.class_id === classId);
    return arr;
  }, [students, scope, classId]);

  const toggleStudent = (sid) => {
    setSelectedStudents(prev => {
      const next = { ...prev };
      if (next[sid]) delete next[sid];
      else next[sid] = { amount: structure?.amount, currency: '' };
      return next;
    });
  };

  const updateOverride = (sid, field, value) => {
    setSelectedStudents(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), [field]: value } }));
  };

  const assignMutation = useMutation({
    mutationFn: (payload) => api(`/tenants/${tenantId}/fee-assign`, { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: (data) => {
      toast.success(`Assigned to ${data.created} students (${data.skipped} already had it)`);
      onAssigned && onAssigned();
    },
    onError: (e) => toast.error(e.message),
  });

  const submit = () => {
    if (!structure) return;
    const overrides = {};
    Object.entries(selectedStudents).forEach(([sid, o]) => {
      const entry = {};
      if (o.amount && Number(o.amount) !== Number(structure.amount)) entry.amount = Number(o.amount);
      if (o.currency) entry.currency = o.currency;
      if (Object.keys(entry).length) overrides[sid] = entry;
    });
    const payload = { fee_structure_id: structure.id };
    if (defaultCurrency) payload.default_currency = defaultCurrency;
    if (Object.keys(overrides).length) payload.student_overrides = overrides;
    if (scope === 'class' && classId) {
      payload.class_id = classId;
    } else if (scope === 'specific') {
      payload.student_ids = Object.keys(selectedStudents);
      if (payload.student_ids.length === 0) {
        toast.error('Select at least one student');
        return;
      }
    }
    assignMutation.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="assign-fee-dialog">
        <DialogHeader>
          <DialogTitle>Assign fee · {structure?.fee_type_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Who to charge</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger data-testid="assign-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="class">All students in a class (bulk)</SelectItem>
                  <SelectItem value="specific">Specific students (individual)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Default currency</Label>
              <Select value={defaultCurrency || '__base__'} onValueChange={v => setDefaultCurrency(v === '__base__' ? '' : v)}>
                <SelectTrigger data-testid="assign-default-currency"><SelectValue placeholder="School base currency" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__base__">School base currency</SelectItem>
                  {(currencies || []).map(c => (
                    <SelectItem key={c.code} value={c.code}>{c.code} — {c.symbol || ''} {c.name || ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {scope === 'class' && (
            <div>
              <Label>Class</Label>
              <Select value={classId} onValueChange={setClassId}>
                <SelectTrigger data-testid="assign-class-select"><SelectValue placeholder="Choose class" /></SelectTrigger>
                <SelectContent>
                  {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                All active students in this class will be charged {fmt(structure?.amount || 0)} in
                {' '}{defaultCurrency || 'the school base currency'}.
              </p>
            </div>
          )}

          {scope === 'specific' && (
            <div className="border rounded-md">
              <div className="p-2 bg-slate-50 text-xs text-slate-600 border-b">
                Select students and (optionally) override amount & currency per student.
              </div>
              <div className="max-h-72 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Student</TableHead>
                      <TableHead className="w-32">Amount</TableHead>
                      <TableHead className="w-32">Currency</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {studentList.length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-6">No students</TableCell></TableRow>
                    )}
                    {studentList.map(s => {
                      const checked = !!selectedStudents[s.id];
                      const ov = selectedStudents[s.id] || {};
                      return (
                        <TableRow key={s.id}>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleStudent(s.id)}
                              data-testid={`assign-student-cb-${s.id}`}
                            />
                          </TableCell>
                          <TableCell className="text-sm">
                            {s.first_name} {s.last_name}
                            <span className="text-xs text-slate-400 ml-1">{s.admission_number}</span>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number" step="0.01" min={0} disabled={!checked}
                              value={ov.amount ?? structure?.amount ?? ''}
                              onChange={e => updateOverride(s.id, 'amount', e.target.value)}
                              className="h-8 text-sm"
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              disabled={!checked}
                              value={ov.currency || '__default__'}
                              onValueChange={v => updateOverride(s.id, 'currency', v === '__default__' ? '' : v)}
                            >
                              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Default" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__default__">Use default</SelectItem>
                                {(currencies || []).map(c => (
                                  <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="p-2 text-xs text-slate-500 border-t">
                {Object.keys(selectedStudents).length} student(s) selected.
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={submit} disabled={assignMutation.isPending} data-testid="assign-fee-submit">
            {assignMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== PAYMENTS ====================
const FeePayments = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ student_id: '', amount: 0, payment_method: 'cash', reference: '', notes: '', payment_date: new Date().toISOString().slice(0, 10) });

  const { data: students } = useQuery({
    queryKey: ['school-students-list', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/students?limit=500`),
    enabled: !!tenantId,
  });

  const { data: paymentsData, isLoading } = useQuery({
    queryKey: ['fee-payments', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/fee-payments?limit=100`),
    enabled: !!tenantId,
  });

  const payMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/fee-payments`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (data) => {
      toast.success(`Payment recorded. Receipt: ${data.receipt_number}`);
      setShowForm(false);
      setForm({ student_id: '', amount: 0, payment_method: 'cash', reference: '', notes: '', payment_date: new Date().toISOString().slice(0, 10) });
      queryClient.invalidateQueries({ queryKey: ['fee-payments'] });
      queryClient.invalidateQueries({ queryKey: ['fee-dashboard'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const studentList = students?.students || [];
  const payments = paymentsData?.payments || [];

  const filteredPayments = search
    ? payments.filter(p => (p.student_name || '').toLowerCase().includes(search.toLowerCase()) || (p.receipt_number || '').toLowerCase().includes(search.toLowerCase()))
    : payments;

  return (
    <div className="space-y-4" data-testid="fee-payments-panel">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search by name or receipt..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Button size="sm" onClick={() => setShowForm(true)} data-testid="record-payment-btn">
          <Plus className="h-4 w-4 mr-1" /> Record Payment
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Receipt #</TableHead>
              <TableHead>Student</TableHead>
              <TableHead>Admission #</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead>Date</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
            ) : filteredPayments.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">No payments recorded yet</TableCell></TableRow>
            ) : filteredPayments.map(p => (
              <TableRow key={p.id}>
                <TableCell className="font-mono text-xs">{p.receipt_number}</TableCell>
                <TableCell className="font-medium">{p.student_name || '-'}</TableCell>
                <TableCell className="text-sm">{p.admission_number || '-'}</TableCell>
                <TableCell className="font-semibold text-emerald-700">{fmt(p.amount)}</TableCell>
                <TableCell><Badge variant="outline" className="capitalize">{(p.payment_method || '').replace(/_/g, ' ')}</Badge></TableCell>
                <TableCell className="text-sm text-slate-500">{p.reference || '-'}</TableCell>
                <TableCell className="text-sm text-slate-500">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => downloadFile(`/tenants/${tenantId}/fee-reports/receipt/${p.id}`, `Receipt_${p.receipt_number}.pdf`)}
                          title="Download PDF Receipt" data-testid={`download-receipt-${p.id}`}>
                    <Download className="h-4 w-4 text-indigo-500" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Student *</Label>
              <Select value={form.student_id} onValueChange={v => setForm({ ...form, student_id: v })}>
                <SelectTrigger data-testid="payment-student"><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent>
                  {studentList.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.admission_number})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount *</Label>
              <Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} min={0} step={0.01} data-testid="payment-amount" />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={form.payment_method} onValueChange={v => setForm({ ...form, payment_method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference / Transaction ID</Label>
              <Input value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} placeholder="e.g. TXN-12345" />
            </div>
            <div>
              <Label>Payment Date *</Label>
              <Input
                type="date" value={form.payment_date}
                onChange={e => setForm({ ...form, payment_date: e.target.value })}
                max={new Date().toISOString().slice(0, 10)}
                data-testid="payment-date"
              />
              <p className="text-[10px] text-slate-500 mt-1">You can back-date for cash collected earlier.</p>
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => payMutation.mutate(form)} disabled={payMutation.isPending || !form.student_id || !form.amount} data-testid="submit-payment-btn">
              {payMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== STUDENT ACCOUNTS ====================
const StudentAccounts = ({ tenantId }) => {
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [search, setSearch] = useState('');

  const { data: students } = useQuery({
    queryKey: ['school-students-list', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/students?limit=500`),
    enabled: !!tenantId,
  });

  const { data: account, isLoading: accountLoading } = useQuery({
    queryKey: ['fee-account', tenantId, selectedStudent],
    queryFn: () => api(`/tenants/${tenantId}/fee-account/${selectedStudent}`),
    enabled: !!selectedStudent,
  });

  const studentList = students?.students || [];
  const filtered = search
    ? studentList.filter(s => `${s.first_name} ${s.last_name} ${s.admission_number}`.toLowerCase().includes(search.toLowerCase()))
    : studentList;

  return (
    <div className="space-y-4" data-testid="student-accounts-panel">
      <div className="flex gap-4">
        {/* Student list */}
        <div className="w-72 shrink-0 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search students..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
          </div>
          <div className="border rounded-lg max-h-[500px] overflow-auto">
            {filtered.map(s => (
              <button key={s.id} onClick={() => setSelectedStudent(s.id)}
                      className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 hover:bg-slate-50 transition-colors ${selectedStudent === s.id ? 'bg-indigo-50 text-indigo-700' : ''}`}
                      data-testid={`student-account-${s.id}`}>
                <div className="font-medium">{s.first_name} {s.last_name}</div>
                <div className="text-xs text-slate-400">{s.admission_number}</div>
              </button>
            ))}
            {filtered.length === 0 && <p className="text-center py-4 text-sm text-slate-400">No students</p>}
          </div>
        </div>

        {/* Account detail */}
        <div className="flex-1">
          {!selectedStudent ? (
            <Card className="flex items-center justify-center py-16 text-slate-400">Select a student to view their fee account</Card>
          ) : accountLoading ? (
            <Card className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></Card>
          ) : account ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-lg">{account.student.name}</h3>
                  <p className="text-sm text-slate-500">{account.student.admission_number}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400">Balance</p>
                  <p className={`text-2xl font-bold ${account.balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmt(account.balance)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Card className="bg-slate-50"><CardContent className="pt-3 pb-2 text-center"><p className="text-xs text-slate-500">Charges</p><p className="font-bold">{fmt(account.total_charges)}</p></CardContent></Card>
                <Card className="bg-emerald-50"><CardContent className="pt-3 pb-2 text-center"><p className="text-xs text-slate-500">Paid</p><p className="font-bold text-emerald-700">{fmt(account.total_payments)}</p></CardContent></Card>
                <Card className={account.balance > 0 ? 'bg-red-50' : 'bg-emerald-50'}><CardContent className="pt-3 pb-2 text-center"><p className="text-xs text-slate-500">Balance</p><p className={`font-bold ${account.balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmt(account.balance)}</p></CardContent></Card>
              </div>

              {/* Charges */}
              <Card>
                <CardHeader><CardTitle className="text-sm">Charges</CardTitle></CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Description</TableHead><TableHead>Amount</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {account.charges.map((c, i) => (
                        <TableRow key={i}>
                          <TableCell>{c.fee_type_name}</TableCell>
                          <TableCell className="text-sm text-slate-500">{c.description || '-'}</TableCell>
                          <TableCell className={c.amount < 0 ? 'text-emerald-600' : 'font-medium'}>{fmt(c.amount)}</TableCell>
                          <TableCell className="text-sm text-slate-400">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}</TableCell>
                        </TableRow>
                      ))}
                      {account.charges.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-4 text-slate-400">No charges</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Payments */}
              <Card>
                <CardHeader><CardTitle className="text-sm">Payments</CardTitle></CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader><TableRow><TableHead>Receipt</TableHead><TableHead>Amount</TableHead><TableHead>Method</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {account.payments.map((p, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono text-xs">{p.receipt_number}</TableCell>
                          <TableCell className="font-semibold text-emerald-700">{fmt(p.amount)}</TableCell>
                          <TableCell className="capitalize">{(p.payment_method || '').replace(/_/g, ' ')}</TableCell>
                          <TableCell className="text-sm text-slate-400">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}</TableCell>
                        </TableRow>
                      ))}
                      {account.payments.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-4 text-slate-400">No payments</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// ==================== DEFAULTERS ====================
const FeeDefaulters = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const { termId: defTermId } = useAcademicContext(tenantId);
  const { data: defaulters = [], isLoading } = useQuery({
    queryKey: ['fee-defaulters', tenantId, defTermId],
    queryFn: () => api(`/tenants/${tenantId}/fee-defaulters${defTermId ? `?term_id=${defTermId}` : ''}`),
    enabled: !!tenantId,
  });

  const lockMutation = useMutation({
    mutationFn: (studentId) => api(`/tenants/${tenantId}/fee-lockout/lock/${studentId}`, { method: 'POST' }),
    onSuccess: () => { toast.success('Student locked'); queryClient.invalidateQueries({ queryKey: ['fee-defaulters'] }); },
    onError: (e) => toast.error(e.message),
  });
  const unlockMutation = useMutation({
    mutationFn: (studentId) => api(`/tenants/${tenantId}/fee-lockout/unlock/${studentId}`, { method: 'POST' }),
    onSuccess: () => { toast.success('Student unlocked'); queryClient.invalidateQueries({ queryKey: ['fee-defaulters'] }); },
    onError: (e) => toast.error(e.message),
  });
  const applyAllMutation = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/fee-lockout/apply`, { method: 'POST' }),
    onSuccess: (data) => { toast.success(`Locked: ${data.locked}, Unlocked: ${data.unlocked}`); queryClient.invalidateQueries({ queryKey: ['fee-defaulters'] }); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-4" data-testid="fee-defaulters-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-red-500" /> Defaulters</h2>
        <div className="flex items-center gap-2">
          <Badge variant="destructive">{defaulters.length} student{defaulters.length !== 1 ? 's' : ''}</Badge>
          <Button variant="outline" size="sm" onClick={() => downloadFile(`/tenants/${tenantId}/fee-reports/defaulters-csv`, 'defaulters.csv')} data-testid="export-defaulters-list">
            <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
          </Button>
          <Button variant="destructive" size="sm" onClick={() => applyAllMutation.mutate()} disabled={applyAllMutation.isPending} data-testid="apply-lockout-all-btn">
            {applyAllMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Lock className="h-3.5 w-3.5 mr-1" />} Lock All Defaulters
          </Button>
        </div>
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Student</TableHead>
              <TableHead>Admission #</TableHead>
              <TableHead>Class</TableHead>
              <TableHead>Outstanding</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
            ) : defaulters.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-emerald-600 font-medium">All fees collected! No defaulters.</TableCell></TableRow>
            ) : defaulters.map(d => (
              <TableRow key={d.student_id}>
                <TableCell className="font-medium">{d.name}</TableCell>
                <TableCell>{d.admission_number}</TableCell>
                <TableCell>{d.class_name || '-'}</TableCell>
                <TableCell className="font-bold text-red-600">{fmt(d.balance)}</TableCell>
                <TableCell className="flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => lockMutation.mutate(d.student_id)}
                          disabled={lockMutation.isPending} title="Lock access" data-testid={`lock-student-${d.student_id}`}>
                    <Lock className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => unlockMutation.mutate(d.student_id)}
                          disabled={unlockMutation.isPending} title="Unlock access" data-testid={`unlock-student-${d.student_id}`}>
                    <Unlock className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// ==================== CARRY FORWARD ====================
const CarryForward = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [fromTerm, setFromTerm] = useState('');
  const [toTerm, setToTerm] = useState('');

  // Opening-balance dialog state
  const [openImport, setOpenImport] = useState(false);
  const [rows, setRows] = useState([{ student_id: '', amount: '', reason: '', as_of_date: new Date().toISOString().slice(0, 10), currency: '' }]);
  const [studentQuery, setStudentQuery] = useState('');

  const { data: terms = [] } = useQuery({
    queryKey: ['school-terms', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/terms`),
    enabled: !!tenantId,
  });

  const { data: studentsData } = useQuery({
    queryKey: ['school-students-min', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/students`),
    enabled: !!tenantId,
  });
  const students = Array.isArray(studentsData) ? studentsData : (studentsData?.students || []);

  const { data: openingBalances = [], refetch: refetchOB } = useQuery({
    queryKey: ['fee-opening-balances', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/fee-opening-balances`),
    enabled: !!tenantId,
  });

  const manualMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/fee-carryforward`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (data) => {
      toast.success(`Carried forward ${data.carried_forward} student balances (${data.zero_balance} had zero balance)`);
      queryClient.invalidateQueries({ queryKey: ['fee-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['fee-defaulters'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const autoMutation = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/fee-auto-carryforward`, { method: 'POST' }),
    onSuccess: (data) => {
      toast.success(`Auto carry forward: ${data.carried_forward} balances from ${data.from_term} to ${data.to_term}`);
      queryClient.invalidateQueries({ queryKey: ['fee-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['fee-defaulters'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const importMutation = useMutation({
    mutationFn: (items) => api(`/tenants/${tenantId}/fee-opening-balance`, { method: 'POST', body: JSON.stringify({ items }) }),
    onSuccess: (data) => {
      toast.success(`Imported ${data.imported} opening balances (skipped ${data.skipped_zero} zero, ${data.skipped_invalid_student} invalid)`);
      queryClient.invalidateQueries({ queryKey: ['fee-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['fee-defaulters'] });
      refetchOB();
      setOpenImport(false);
      setRows([{ student_id: '', amount: '', reason: '', as_of_date: new Date().toISOString().slice(0, 10), currency: '' }]);
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteOBMutation = useMutation({
    mutationFn: (chargeId) => api(`/tenants/${tenantId}/fee-opening-balance/${chargeId}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Opening balance removed'); refetchOB(); queryClient.invalidateQueries({ queryKey: ['fee-defaulters'] }); },
    onError: (e) => toast.error(e.message),
  });

  const addRow = () => setRows([...rows, { student_id: '', amount: '', reason: '', as_of_date: new Date().toISOString().slice(0, 10), currency: '' }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const updateRow = (i, field, value) => setRows(rows.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  const filteredStudents = studentQuery
    ? students.filter(s => `${s.first_name} ${s.last_name} ${s.admission_number || ''}`.toLowerCase().includes(studentQuery.toLowerCase()))
    : students;

  const submitImport = () => {
    const items = rows
      .filter(r => r.student_id && parseFloat(r.amount) > 0)
      .map(r => ({
        student_id: r.student_id,
        amount: parseFloat(r.amount),
        reason: r.reason || 'Opening balance carried from previous system',
        as_of_date: r.as_of_date || undefined,
        currency: r.currency || undefined,
      }));
    if (items.length === 0) { toast.error('Add at least one row with a student and amount'); return; }
    importMutation.mutate(items);
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="carry-forward-panel">
      {/* Auto Carry Forward */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-indigo-500" /> Auto Carry Forward
          </CardTitle>
          <CardDescription>
            Automatically detect the previous and current terms, and carry forward all outstanding balances.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => autoMutation.mutate()} disabled={autoMutation.isPending} data-testid="auto-carryforward-btn">
            {autoMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Auto Carry Forward Balances
          </Button>
        </CardContent>
      </Card>

      {/* Manual Carry Forward */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Manual Carry Forward</CardTitle>
          <CardDescription>Select specific terms to carry forward balances between.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>From Term</Label>
              <Select value={fromTerm} onValueChange={setFromTerm}>
                <SelectTrigger data-testid="carryforward-from"><SelectValue placeholder="Select source term" /></SelectTrigger>
                <SelectContent>{terms.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>To Term</Label>
              <Select value={toTerm} onValueChange={setToTerm}>
                <SelectTrigger data-testid="carryforward-to"><SelectValue placeholder="Select target term" /></SelectTrigger>
                <SelectContent>{terms.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={() => manualMutation.mutate({ from_term_id: fromTerm, to_term_id: toTerm })}
                  disabled={manualMutation.isPending || !fromTerm || !toTerm}
                  data-testid="manual-carryforward-btn">
            {manualMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Carry Forward Selected Terms
          </Button>
        </CardContent>
      </Card>

      {/* Opening Balance / Legacy Debt Import */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-5 w-5 text-emerald-600" /> Opening Balance · Legacy Debt Import
          </CardTitle>
          <CardDescription>
            Bring historical fee balances from a previous school management system onto a student's
            account. Each entry surfaces as an outstanding fee — payments work as normal afterwards.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button onClick={() => setOpenImport(true)} data-testid="open-import-opening-balance-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Import Opening Balances
          </Button>

          {openingBalances.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>As of</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {openingBalances.map(ob => (
                    <TableRow key={ob.id} data-testid={`ob-row-${ob.id}`}>
                      <TableCell className="font-medium">
                        {ob.student_name || '—'}
                        {ob.admission_number && <span className="text-xs text-slate-400 ml-1">#{ob.admission_number}</span>}
                      </TableCell>
                      <TableCell className="font-mono">
                        {ob.currency ? `${ob.currency} ` : ''}{Number(ob.amount).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {ob.as_of_date ? ob.as_of_date.slice(0, 10) : '—'}
                      </TableCell>
                      <TableCell className="max-w-[220px] truncate text-xs text-slate-600" title={ob.reason}>{ob.reason}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          ob.status === 'pending' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          ob.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          'bg-slate-100 text-slate-600'
                        }>{ob.status}</Badge>
                      </TableCell>
                      <TableCell>
                        {ob.status === 'pending' && (
                          <Button
                            variant="ghost" size="icon"
                            className="h-7 w-7 text-red-400 hover:text-red-600"
                            onClick={() => { if (window.confirm('Remove this opening balance entry?')) deleteOBMutation.mutate(ob.id); }}
                            data-testid={`ob-delete-${ob.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import Dialog */}
      <Dialog open={openImport} onOpenChange={setOpenImport}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Opening Balances</DialogTitle>
            <CardDescription>
              Add one row per student. Each row creates a pending fee charge with the
              "as of" date — useful for matching the historical statement from your
              previous system.
            </CardDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Input
              placeholder="Search students by name or admission no..."
              value={studentQuery}
              onChange={e => setStudentQuery(e.target.value)}
              data-testid="ob-student-search"
            />
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end p-2 border rounded">
                <div className="col-span-4">
                  {i === 0 && <Label className="text-xs">Student</Label>}
                  <Select value={r.student_id} onValueChange={(v) => updateRow(i, 'student_id', v)}>
                    <SelectTrigger data-testid={`ob-student-${i}`}><SelectValue placeholder="Select student" /></SelectTrigger>
                    <SelectContent className="max-h-80">
                      {filteredStudents.slice(0, 150).map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.first_name} {s.last_name} {s.admission_number ? `· #${s.admission_number}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  {i === 0 && <Label className="text-xs">Amount</Label>}
                  <Input type="number" min="0" step="0.01" value={r.amount} onChange={e => updateRow(i, 'amount', e.target.value)} placeholder="0.00" data-testid={`ob-amount-${i}`} />
                </div>
                <div className="col-span-1">
                  {i === 0 && <Label className="text-xs">Curr</Label>}
                  <Input value={r.currency} onChange={e => updateRow(i, 'currency', e.target.value.toUpperCase())} maxLength={4} placeholder="USD" data-testid={`ob-currency-${i}`} />
                </div>
                <div className="col-span-2">
                  {i === 0 && <Label className="text-xs">As of</Label>}
                  <Input type="date" value={r.as_of_date} max={new Date().toISOString().slice(0, 10)} onChange={e => updateRow(i, 'as_of_date', e.target.value)} data-testid={`ob-date-${i}`} />
                </div>
                <div className="col-span-2">
                  {i === 0 && <Label className="text-xs">Reason</Label>}
                  <Input value={r.reason} onChange={e => updateRow(i, 'reason', e.target.value)} placeholder="From XYZ system" data-testid={`ob-reason-${i}`} />
                </div>
                <div className="col-span-1 flex justify-end">
                  {rows.length > 1 && (
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-red-400" onClick={() => removeRow(i)} data-testid={`ob-remove-row-${i}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addRow} data-testid="ob-add-row-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add another student
            </Button>
          </div>

          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={submitImport} disabled={importMutation.isPending} data-testid="ob-submit-btn">
              {importMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
              Import {rows.filter(r => r.student_id && parseFloat(r.amount) > 0).length || ''} Balance{rows.length > 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== LOCKOUT SETTINGS ====================
const LOCKABLE = [
  { id: 'all', label: 'BLOCK ALL PORTAL ACCESS', description: 'Full portal blocked — student sees only "outstanding balance" screen' },
  { id: 'timetable', label: 'Timetable' },
  { id: 'grades', label: 'Grades' },
  { id: 'report_card', label: 'Report Cards' },
  { id: 'lms', label: 'LMS / Lessons' },
  { id: 'library', label: 'Library' },
  { id: 'assignments', label: 'Assignments' },
  { id: 'reading_list', label: 'Reading List' },
  { id: 'live_classes', label: 'Live Classes' },
];

const LockoutSettings = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const { data: settings, isLoading } = useQuery({
    queryKey: ['fee-lockout-settings', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/fee-lockout-settings`),
    enabled: !!tenantId,
  });

  const [form, setForm] = useState(null);

  React.useEffect(() => {
    if (settings && !form) {
      setForm({
        enabled: settings.enabled || false,
        auto_lock: settings.auto_lock || false,
        locked_features: settings.locked_features || [],
        grace_days: settings.grace_days || 0,
        lock_message: settings.lock_message || '',
      });
    }
  }, [settings, form]);

  const saveMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/fee-lockout-settings`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => { toast.success('Lockout settings saved'); queryClient.invalidateQueries({ queryKey: ['fee-lockout-settings'] }); },
    onError: (e) => toast.error(e.message),
  });

  const toggleFeature = (featureId) => {
    setForm(prev => ({
      ...prev,
      locked_features: prev.locked_features.includes(featureId)
        ? prev.locked_features.filter(f => f !== featureId)
        : [...prev.locked_features, featureId],
    }));
  };

  if (isLoading || !form) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="lockout-settings-panel">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-red-500" /> Fee Lockout Settings
          </CardTitle>
          <CardDescription>
            Restrict student access to certain features when they have outstanding fees.
            Students can still log in, but locked features will show a restriction notice.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">Enable Fee Lockout</Label>
              <p className="text-sm text-slate-500">When enabled, defaulting students will be restricted</p>
            </div>
            <Switch checked={form.enabled} onCheckedChange={v => setForm({ ...form, enabled: v })} data-testid="lockout-enable-switch" />
          </div>

          {form.enabled && (
            <>
              {/* Auto Lock */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">Auto Lock</Label>
                  <p className="text-sm text-slate-500">Automatically lock when "Lock All Defaulters" is used</p>
                </div>
                <Switch checked={form.auto_lock} onCheckedChange={v => setForm({ ...form, auto_lock: v })} />
              </div>

              {/* Features to Lock */}
              <div>
                <Label className="text-base font-medium mb-3 block">Features to Restrict</Label>
                <div className="grid grid-cols-2 gap-3">
                  {LOCKABLE.map(f => (
                    <div key={f.id}
                         className={`border rounded-lg p-3 cursor-pointer transition-all ${form.locked_features.includes(f.id) ? 'border-red-400 bg-red-50' : 'border-slate-200 hover:border-slate-300'} ${f.id === 'all' ? 'col-span-2 border-red-300' : ''}`}
                         onClick={() => toggleFeature(f.id)}
                         data-testid={`lockout-feature-${f.id}`}>
                      <div className="flex items-center gap-2">
                        {form.locked_features.includes(f.id) ? <Lock className="h-4 w-4 text-red-500" /> : <Unlock className="h-4 w-4 text-slate-400" />}
                        <span className={`font-medium text-sm ${f.id === 'all' ? 'text-red-700' : ''}`}>{f.label}</span>
                      </div>
                      {f.description && (
                        <p className="text-[11px] text-slate-500 mt-1 ml-6">{f.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Grace days */}
              <div>
                <Label>Grace Days (after due date)</Label>
                <Input type="number" value={form.grace_days} onChange={e => setForm({ ...form, grace_days: parseInt(e.target.value) || 0 })} min={0} className="max-w-[150px]" />
                <p className="text-xs text-slate-400 mt-1">Number of days after fee due date before lockout applies</p>
              </div>

              {/* Lock message */}
              <div>
                <Label>Lockout Message</Label>
                <textarea className="w-full border rounded-lg p-3 text-sm min-h-[80px] focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          value={form.lock_message} onChange={e => setForm({ ...form, lock_message: e.target.value })}
                          placeholder="Message shown to locked students..." data-testid="lockout-message" />
              </div>
            </>
          )}

          <Button onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending} data-testid="save-lockout-settings-btn">
            {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save Lockout Settings
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== FEE GROUPS ====================
const FeeGroups = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', description: '', fee_type_ids: [], total_amount: '' });

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ['fee-groups', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/fee-groups`),
    enabled: !!tenantId,
  });

  const { data: feeTypes = [] } = useQuery({
    queryKey: ['fee-types', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/fee-types`),
    enabled: !!tenantId,
  });

  const saveMutation = useMutation({
    mutationFn: (d) => editing ? api(`/tenants/${tenantId}/fee-groups/${editing.id}`, { method: 'PUT', body: JSON.stringify(d) }) : api(`/tenants/${tenantId}/fee-groups`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success(editing ? 'Group updated' : 'Group created'); setShowForm(false); setEditing(null); queryClient.invalidateQueries({ queryKey: ['fee-groups'] }); },
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/fee-groups/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Group removed'); queryClient.invalidateQueries({ queryKey: ['fee-groups'] }); },
  });

  const openEdit = (g) => {
    setEditing(g);
    setForm({ name: g.name, code: g.code, description: g.description || '', fee_type_ids: g.fee_type_ids || [], total_amount: g.total_amount || '' });
    setShowForm(true);
  };

  return (
    <div className="space-y-4" data-testid="fee-groups">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Fee Groups</h3>
        <Button size="sm" onClick={() => { setEditing(null); setForm({ name: '', code: '', description: '', fee_type_ids: [], total_amount: '' }); setShowForm(true); }} data-testid="add-fee-group-btn"><Plus className="h-4 w-4 mr-1" /> Add Group</Button>
      </div>
      {isLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : groups.length === 0 ? <p className="text-sm text-slate-400 text-center py-8">No fee groups. Create one to bundle fee types together.</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {groups.map(g => (
            <Card key={g.id} data-testid={`fee-group-${g.id}`}>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold">{g.name}</h4>
                    <p className="text-xs text-slate-500 font-mono">{g.code}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(g)}><Settings className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => { if (window.confirm('Remove this group?')) deleteMutation.mutate(g.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                {g.description && <p className="text-xs text-slate-600 mt-1">{g.description}</p>}
                <div className="mt-2 flex flex-wrap gap-1">
                  {g.fee_types?.map(ft => <Badge key={ft.id} variant="outline" className="text-xs">{ft.name} (${fmt(ft.default_amount)})</Badge>)}
                </div>
                <div className="mt-2 flex items-center gap-3 text-sm">
                  <span className="font-bold text-emerald-700">${fmt(g.total_amount || g.computed_total)}</span>
                  <span className="text-xs text-slate-400">Classes: {g.class_names?.join(', ')}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); setEditing(null); } }}>
        <DialogContent aria-describedby="fg-desc">
          <DialogHeader><DialogTitle>{editing ? 'Edit Fee Group' : 'New Fee Group'}</DialogTitle></DialogHeader>
          <p id="fg-desc" className="sr-only">Fee group form</p>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="fee-group-name" /></div>
              <div><Label>Code *</Label><Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="fee-group-code" /></div>
            </div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <div>
              <Label>Fee Types in Group</Label>
              <div className="grid grid-cols-2 gap-1 mt-1 max-h-40 overflow-y-auto">
                {feeTypes.map(ft => (
                  <label key={ft.id} className="flex items-center gap-2 text-sm cursor-pointer p-1 rounded hover:bg-slate-50">
                    <input type="checkbox" checked={form.fee_type_ids.includes(ft.id)} onChange={e => {
                      setForm({ ...form, fee_type_ids: e.target.checked ? [...form.fee_type_ids, ft.id] : form.fee_type_ids.filter(id => id !== ft.id) });
                    }} />
                    <span>{ft.name} <span className="text-slate-400">(${fmt(ft.default_amount)})</span></span>
                  </label>
                ))}
              </div>
            </div>
            <div><Label>Override Total Amount (optional)</Label><Input type="number" value={form.total_amount} onChange={e => setForm({ ...form, total_amount: e.target.value ? parseFloat(e.target.value) : '' })} placeholder="Leave empty to sum fee types" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate(form)} disabled={!form.name || !form.code || saveMutation.isPending} data-testid="fee-group-submit">{saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==================== DISCOUNT ENGINE ====================
const DiscountEngine = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', discount_type: 'percentage', value: '', criteria: 'manual', auto_apply: false, description: '', max_amount: '' });
  const [showApply, setShowApply] = useState(null);
  const [applyStudentId, setApplyStudentId] = useState('');

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['discount-rules', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/discount-rules`),
    enabled: !!tenantId,
  });

  const { data: applications = [] } = useQuery({
    queryKey: ['discount-applications', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/discount-applications`),
    enabled: !!tenantId,
  });

  const { data: studentData } = useQuery({
    queryKey: ['school-students-list', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/students?limit=200`),
    enabled: !!tenantId,
  });
  const students = studentData?.students || [];

  const saveMutation = useMutation({
    mutationFn: (d) => editing ? api(`/tenants/${tenantId}/discount-rules/${editing.id}`, { method: 'PUT', body: JSON.stringify(d) }) : api(`/tenants/${tenantId}/discount-rules`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success(editing ? 'Rule updated' : 'Rule created'); setShowForm(false); setEditing(null); queryClient.invalidateQueries({ queryKey: ['discount-rules'] }); },
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/discount-rules/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Rule removed'); queryClient.invalidateQueries({ queryKey: ['discount-rules'] }); },
  });

  const applyMutation = useMutation({
    mutationFn: (d) => api(`/tenants/${tenantId}/discount-apply`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: (data) => { toast.success(`Discount applied: $${fmt(data.discount_amount)}`); setShowApply(null); setApplyStudentId(''); queryClient.invalidateQueries({ queryKey: ['discount-applications'] }); queryClient.invalidateQueries({ queryKey: ['fee-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });

  const CRITERIA = [
    { value: 'manual', label: 'Manual' },
    { value: 'sibling', label: 'Sibling' },
    { value: 'staff_child', label: 'Staff Child' },
    { value: 'merit', label: 'Merit Based' },
    { value: 'scholarship', label: 'Scholarship' },
  ];

  return (
    <div className="space-y-4" data-testid="discount-engine">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Discount Rules</h3>
        <Button size="sm" onClick={() => { setEditing(null); setForm({ name: '', code: '', discount_type: 'percentage', value: '', criteria: 'manual', auto_apply: false, description: '', max_amount: '' }); setShowForm(true); }} data-testid="add-discount-rule-btn"><Plus className="h-4 w-4 mr-1" /> Add Rule</Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Rule</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Value</TableHead>
              <TableHead>Criteria</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? <TableRow><TableCell colSpan={5} className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow> :
            rules.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-400">No discount rules</TableCell></TableRow> :
            rules.map(r => (
              <TableRow key={r.id} data-testid={`discount-rule-${r.id}`}>
                <TableCell><div><span className="font-medium">{r.name}</span><p className="text-xs text-slate-400 font-mono">{r.code}</p></div></TableCell>
                <TableCell><Badge variant="outline" className="capitalize">{r.discount_type?.replace('_', ' ')}</Badge></TableCell>
                <TableCell className="font-semibold">{r.discount_type === 'percentage' ? `${r.value}%` : `$${fmt(r.value)}`}{r.max_amount ? <span className="text-xs text-slate-400 ml-1">(max ${fmt(r.max_amount)})</span> : ''}</TableCell>
                <TableCell><Badge className="capitalize text-xs">{r.criteria?.replace('_', ' ')}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button variant="outline" size="sm" className="mr-1" onClick={() => setShowApply(r)} data-testid={`apply-discount-${r.id}`}>Apply</Button>
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(r); setForm({ name: r.name, code: r.code, discount_type: r.discount_type, value: r.value, criteria: r.criteria, auto_apply: r.auto_apply || false, description: r.description || '', max_amount: r.max_amount || '' }); setShowForm(true); }}><Settings className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => { if (window.confirm('Delete this rule?')) deleteMutation.mutate(r.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {applications.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Recent Discount Applications</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1">
              {applications.slice(0, 10).map(a => (
                <div key={a.id} className="flex items-center justify-between text-sm bg-slate-50 rounded px-3 py-1.5">
                  <span>{a.student_name} - <span className="text-slate-500">{a.rule_name}</span></span>
                  <span className="font-medium text-emerald-700">-${fmt(a.discount_amount)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rule Form */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); setEditing(null); } }}>
        <DialogContent aria-describedby="dr-desc">
          <DialogHeader><DialogTitle>{editing ? 'Edit Discount Rule' : 'New Discount Rule'}</DialogTitle></DialogHeader>
          <p id="dr-desc" className="sr-only">Discount rule form</p>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="discount-name" /></div>
              <div><Label>Code *</Label><Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="discount-code" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <Select value={form.discount_type} onValueChange={v => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percentage">Percentage (%)</SelectItem><SelectItem value="fixed_amount">Fixed Amount ($)</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Value *</Label><Input type="number" value={form.value} onChange={e => setForm({ ...form, value: parseFloat(e.target.value) || 0 })} placeholder={form.discount_type === 'percentage' ? 'e.g. 10' : 'e.g. 500'} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Criteria</Label>
                <Select value={form.criteria} onValueChange={v => setForm({ ...form, criteria: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CRITERIA.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Max Discount Cap</Label><Input type="number" value={form.max_amount} onChange={e => setForm({ ...form, max_amount: e.target.value ? parseFloat(e.target.value) : '' })} placeholder="No cap" /></div>
            </div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate(form)} disabled={!form.name || !form.code || !form.value || saveMutation.isPending} data-testid="discount-submit">{saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply Discount Dialog */}
      <Dialog open={!!showApply} onOpenChange={() => { setShowApply(null); setApplyStudentId(''); }}>
        <DialogContent aria-describedby="apply-desc">
          <DialogHeader><DialogTitle>Apply: {showApply?.name}</DialogTitle></DialogHeader>
          <p id="apply-desc" className="sr-only">Apply discount</p>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">{showApply?.discount_type === 'percentage' ? `${showApply?.value}% off` : `$${fmt(showApply?.value)} off`} - {showApply?.description}</p>
            <div>
              <Label>Select Student *</Label>
              <Select value={applyStudentId || "none"} onValueChange={v => setApplyStudentId(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="apply-student-select"><SelectValue placeholder="Choose student" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select a student</SelectItem>
                  {students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.admission_number})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowApply(null); setApplyStudentId(''); }}>Cancel</Button>
            <Button onClick={() => applyMutation.mutate({ student_id: applyStudentId, discount_rule_id: showApply?.id })} disabled={!applyStudentId || applyMutation.isPending} data-testid="apply-discount-submit">
              {applyMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Apply Discount
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==================== INSTALLMENT PLANS ====================
const InstallmentPlans = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', total_amount: '', installments: [{ due_date: '', amount: '', label: 'Installment 1' }] });

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['installment-plans', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/installment-plans`),
    enabled: !!tenantId,
  });

  const saveMutation = useMutation({
    mutationFn: (d) => editing ? api(`/tenants/${tenantId}/installment-plans/${editing.id}`, { method: 'PUT', body: JSON.stringify(d) }) : api(`/tenants/${tenantId}/installment-plans`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success(editing ? 'Plan updated' : 'Plan created'); setShowForm(false); setEditing(null); queryClient.invalidateQueries({ queryKey: ['installment-plans'] }); },
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/installment-plans/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Plan removed'); queryClient.invalidateQueries({ queryKey: ['installment-plans'] }); },
  });

  const addInstallment = () => {
    setForm({ ...form, installments: [...form.installments, { due_date: '', amount: '', label: `Installment ${form.installments.length + 1}` }] });
  };

  const removeInstallment = (idx) => {
    setForm({ ...form, installments: form.installments.filter((_, i) => i !== idx) });
  };

  const updateInstallment = (idx, field, value) => {
    const updated = [...form.installments];
    updated[idx] = { ...updated[idx], [field]: field === 'amount' ? (parseFloat(value) || 0) : value };
    setForm({ ...form, installments: updated });
  };

  const instTotal = form.installments.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);

  return (
    <div className="space-y-4" data-testid="installment-plans">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Installment Plans</h3>
        <Button size="sm" onClick={() => { setEditing(null); setForm({ name: '', total_amount: '', installments: [{ due_date: '', amount: '', label: 'Installment 1' }] }); setShowForm(true); }} data-testid="add-installment-plan-btn"><Plus className="h-4 w-4 mr-1" /> Add Plan</Button>
      </div>

      {isLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : plans.length === 0 ? <p className="text-sm text-slate-400 text-center py-8">No installment plans. Create one to split fees into scheduled payments.</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {plans.map(p => (
            <Card key={p.id} data-testid={`installment-plan-${p.id}`}>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold">{p.name}</h4>
                    <p className="text-lg font-bold text-emerald-700 mt-1">${fmt(p.total_amount)}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(p); setForm({ name: p.name, total_amount: p.total_amount, installments: p.installments || [] }); setShowForm(true); }}><Settings className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => { if (window.confirm('Delete this plan?')) deleteMutation.mutate(p.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                <div className="mt-2 space-y-1">
                  {(p.installments || []).map((inst, i) => (
                    <div key={i} className="flex items-center justify-between text-sm bg-slate-50 rounded px-2 py-1">
                      <span>{inst.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">{inst.due_date}</span>
                        <span className="font-medium">${fmt(inst.amount)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">{p.assigned_students} student{p.assigned_students !== 1 ? 's' : ''} assigned</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); setEditing(null); } }}>
        <DialogContent className="max-w-lg" aria-describedby="ip-desc">
          <DialogHeader><DialogTitle>{editing ? 'Edit Installment Plan' : 'New Installment Plan'}</DialogTitle></DialogHeader>
          <p id="ip-desc" className="sr-only">Installment plan form</p>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Plan Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="installment-name" /></div>
              <div><Label>Total Amount *</Label><Input type="number" value={form.total_amount} onChange={e => setForm({ ...form, total_amount: parseFloat(e.target.value) || 0 })} data-testid="installment-total" /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Installments</Label>
                <Button variant="outline" size="sm" onClick={addInstallment}><Plus className="h-3 w-3 mr-1" /> Add</Button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {form.installments.map((inst, i) => (
                  <div key={i} className="grid grid-cols-[1fr_100px_100px_32px] gap-2 items-center">
                    <Input value={inst.label} onChange={e => updateInstallment(i, 'label', e.target.value)} placeholder="Label" className="text-sm" />
                    <Input type="date" value={inst.due_date} onChange={e => updateInstallment(i, 'due_date', e.target.value)} className="text-sm" />
                    <Input type="number" value={inst.amount} onChange={e => updateInstallment(i, 'amount', e.target.value)} placeholder="$" className="text-sm" />
                    <Button variant="ghost" size="sm" className="text-red-400 px-1" onClick={() => removeInstallment(i)} disabled={form.installments.length <= 1}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                ))}
              </div>
              <div className={`text-xs mt-1 ${Math.abs(instTotal - (form.total_amount || 0)) > 0.01 ? 'text-red-500' : 'text-green-600'}`}>
                Installments sum: ${fmt(instTotal)} {Math.abs(instTotal - (form.total_amount || 0)) > 0.01 ? `(must equal $${fmt(form.total_amount)})` : '(matches total)'}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate(form)} disabled={!form.name || !form.total_amount || Math.abs(instTotal - (form.total_amount || 0)) > 0.01 || saveMutation.isPending} data-testid="installment-submit">
              {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==================== ADHOC CHARGES TAB ====================
// One-off fees (broken window, library fine, lost ID, exam re-sit, etc.)
const AdhocCharges = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    student_id: '', amount: '', description: '', category: 'damage',
    incident_date: new Date().toISOString().slice(0, 10),
    due_date: '', currency: '',
  });
  const [studentQuery, setStudentQuery] = useState('');

  const { data: studentsData } = useQuery({
    queryKey: ['school-students-min', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/students`),
    enabled: !!tenantId,
  });
  const students = Array.isArray(studentsData) ? studentsData : (studentsData?.students || []);

  const { data: charges = [], refetch } = useQuery({
    queryKey: ['fee-adhoc-charges', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/fee-adhoc-charges`),
    enabled: !!tenantId,
  });

  const createMutation = useMutation({
    mutationFn: (payload) => api(`/tenants/${tenantId}/fee-adhoc-charges`, { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: () => {
      toast.success('Charge added — appears on student statement immediately');
      queryClient.invalidateQueries({ queryKey: ['fee-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['fee-defaulters'] });
      refetch();
      setDialogOpen(false);
      setForm({ student_id: '', amount: '', description: '', category: 'damage', incident_date: new Date().toISOString().slice(0, 10), due_date: '', currency: '' });
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/fee-adhoc-charges/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Charge removed'); refetch(); queryClient.invalidateQueries({ queryKey: ['fee-defaulters'] }); },
    onError: (e) => toast.error(e.message),
  });

  const filteredStudents = studentQuery
    ? students.filter(s => `${s.first_name} ${s.last_name} ${s.admission_number || ''}`.toLowerCase().includes(studentQuery.toLowerCase()))
    : students;

  const submit = () => {
    if (!form.student_id) { toast.error('Pick a student'); return; }
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error('Amount must be greater than 0'); return; }
    if (!form.description.trim()) { toast.error('Describe what the charge is for'); return; }
    createMutation.mutate({
      student_id: form.student_id,
      amount: amt,
      description: form.description.trim(),
      category: form.category,
      incident_date: form.incident_date || undefined,
      due_date: form.due_date || undefined,
      currency: form.currency || undefined,
    });
  };

  const categoryStyle = {
    damage: 'bg-red-50 text-red-700 border-red-200',
    fine: 'bg-amber-50 text-amber-700 border-amber-200',
    replacement: 'bg-orange-50 text-orange-700 border-orange-200',
    extra_service: 'bg-blue-50 text-blue-700 border-blue-200',
    other: 'bg-slate-100 text-slate-600 border-slate-200',
  };

  return (
    <div className="space-y-4 max-w-5xl" data-testid="adhoc-charges-panel">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-rose-500" /> Ad-hoc Fees & Charges
              </CardTitle>
              <CardDescription>
                Add one-off charges to a student — broken windows, library fines, lost ID cards,
                exam re-sit fees, field-trip top-ups, etc. They appear on the student statement and
                can be paid via the normal fee-payment flow.
              </CardDescription>
            </div>
            <Button onClick={() => setDialogOpen(true)} className="shrink-0" data-testid="add-adhoc-charge-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Add Charge
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {charges.length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">No ad-hoc charges yet.</p>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Incident</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {charges.map(c => (
                    <TableRow key={c.id} data-testid={`adhoc-row-${c.id}`}>
                      <TableCell className="font-medium">
                        {c.student_name || '—'}
                        {c.admission_number && <span className="text-xs text-slate-400 ml-1">#{c.admission_number}</span>}
                      </TableCell>
                      <TableCell className="max-w-[280px] truncate" title={c.description}>{c.description}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={categoryStyle[c.category] || categoryStyle.other}>{c.category}</Badge>
                      </TableCell>
                      <TableCell className="font-mono">
                        {c.currency ? `${c.currency} ` : ''}{Number(c.amount).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{c.incident_date ? c.incident_date.slice(0, 10) : '—'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={c.status === 'pending' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}>{c.status}</Badge>
                      </TableCell>
                      <TableCell>
                        {c.status === 'pending' && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600"
                            onClick={() => { if (window.confirm('Remove this charge?')) deleteMutation.mutate(c.id); }}
                            data-testid={`adhoc-delete-${c.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Ad-hoc Charge</DialogTitle>
            <CardDescription>Examples: broken classroom window, library book replacement, exam re-sit, lost ID card.</CardDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Student</Label>
              <Input
                placeholder="Search by name or admission no..."
                value={studentQuery}
                onChange={(e) => setStudentQuery(e.target.value)}
                className="mb-2"
                data-testid="adhoc-student-search"
              />
              <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                <SelectTrigger data-testid="adhoc-student"><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {filteredStudents.slice(0, 150).map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.first_name} {s.last_name}{s.admission_number ? ` · #${s.admission_number}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Amount</Label>
                <Input type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0.00" data-testid="adhoc-amount" />
              </div>
              <div>
                <Label className="text-xs">Currency</Label>
                <Input value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value.toUpperCase() })} maxLength={4} placeholder="USD" data-testid="adhoc-currency" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Replacement window pane — D2 Block" data-testid="adhoc-description" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="adhoc-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="damage">Damage</SelectItem>
                    <SelectItem value="fine">Fine</SelectItem>
                    <SelectItem value="replacement">Replacement</SelectItem>
                    <SelectItem value="extra_service">Extra service</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Incident Date</Label>
                <Input type="date" max={new Date().toISOString().slice(0, 10)} value={form.incident_date} onChange={e => setForm({ ...form, incident_date: e.target.value })} data-testid="adhoc-incident-date" />
              </div>
              <div>
                <Label className="text-xs">Due Date (optional)</Label>
                <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} data-testid="adhoc-due-date" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={submit} disabled={createMutation.isPending} data-testid="adhoc-submit">
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
              Add Charge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==================== MAIN COMPONENT ====================
export const SchoolFees = ({ tenantId }) => {
  const { campusId, campuses } = useCampusContext(tenantId);
  const currentCampus = campuses.find(c => c.id === campusId);
  return (
    <div data-testid="school-fees-module">
      {campusId && currentCampus && (
        <div className="mb-3 text-xs text-slate-500" data-testid="fees-campus-scope">
          Showing fees & payments scoped to <b>{currentCampus.name}</b>. Switch the campus chip at the top to change.
        </div>
      )}
      <Tabs defaultValue="dashboard" className="space-y-4">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="dashboard" data-testid="fees-tab-dashboard"><TrendingUp className="h-3.5 w-3.5 mr-1" /> Dashboard</TabsTrigger>
          <TabsTrigger value="types" data-testid="fees-tab-types"><DollarSign className="h-3.5 w-3.5 mr-1" /> Fee Types</TabsTrigger>
          <TabsTrigger value="groups" data-testid="fees-tab-groups"><FileText className="h-3.5 w-3.5 mr-1" /> Groups</TabsTrigger>
          <TabsTrigger value="structures" data-testid="fees-tab-structures"><FileText className="h-3.5 w-3.5 mr-1" /> Structures</TabsTrigger>
          <TabsTrigger value="payments" data-testid="fees-tab-payments"><Receipt className="h-3.5 w-3.5 mr-1" /> Payments</TabsTrigger>
          <TabsTrigger value="discounts" data-testid="fees-tab-discounts"><DollarSign className="h-3.5 w-3.5 mr-1" /> Discounts</TabsTrigger>
          <TabsTrigger value="installments" data-testid="fees-tab-installments"><CreditCard className="h-3.5 w-3.5 mr-1" /> Installments</TabsTrigger>
          <TabsTrigger value="accounts" data-testid="fees-tab-accounts"><Users className="h-3.5 w-3.5 mr-1" /> Accounts</TabsTrigger>
          <TabsTrigger value="defaulters" data-testid="fees-tab-defaulters"><AlertTriangle className="h-3.5 w-3.5 mr-1" /> Defaulters</TabsTrigger>
          <TabsTrigger value="carryforward" data-testid="fees-tab-carryforward"><ArrowRightLeft className="h-3.5 w-3.5 mr-1" /> Carry Forward</TabsTrigger>
          <TabsTrigger value="adhoc" data-testid="fees-tab-adhoc"><Plus className="h-3.5 w-3.5 mr-1" /> Ad-hoc Fees</TabsTrigger>
          <TabsTrigger value="lockout" data-testid="fees-tab-lockout"><Shield className="h-3.5 w-3.5 mr-1" /> Lockout</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard"><FeeDashboard tenantId={tenantId} /></TabsContent>
        <TabsContent value="types"><FeeTypes tenantId={tenantId} /></TabsContent>
        <TabsContent value="groups"><FeeGroups tenantId={tenantId} /></TabsContent>
        <TabsContent value="structures"><FeeStructures tenantId={tenantId} /></TabsContent>
        <TabsContent value="payments"><FeePayments tenantId={tenantId} /></TabsContent>
        <TabsContent value="discounts"><DiscountEngine tenantId={tenantId} /></TabsContent>
        <TabsContent value="installments"><InstallmentPlans tenantId={tenantId} /></TabsContent>
        <TabsContent value="accounts"><StudentAccounts tenantId={tenantId} /></TabsContent>
        <TabsContent value="defaulters"><FeeDefaulters tenantId={tenantId} /></TabsContent>
        <TabsContent value="carryforward"><CarryForward tenantId={tenantId} /></TabsContent>
        <TabsContent value="adhoc"><AdhocCharges tenantId={tenantId} /></TabsContent>
        <TabsContent value="lockout"><LockoutSettings tenantId={tenantId} /></TabsContent>
      </Tabs>
    </div>
  );
};

export default SchoolFees;
